<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { FilterMatchMode } from '@primevue/core/api'
import Paginator from 'primevue/paginator'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import CheckBoxDefault from '@/components/forms/Checkboxes/CheckBoxDefault.vue'
import TenderUpload from '@/components/TenderUpload.vue'
import type { Tender, WorkflowStatus } from '@/types/tender'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import AgentAssignmentModal from '../Modals/AgentAssignmentModal.vue'
import RejectionModal from '../Modals/RejectionModal.vue'
import WatchlistModal from '../Modals/WatchlistModal.vue'
import { getBadgeStatus, getWorkflowLabel } from '@/utils/badgeUtils'
import {
  TagIcon,
  RefreshIcon,
  // SigmaIcon,
  // CloneIcon,
  EyeIcon,
  ExternalIcon,
  GlobeIcon,
  AgentIcon,
  UploadIcon,
  BanIcon,
  BookMarkIcon,
} from '@/icons'
import router from '@/router'
// import StatsCards from '@/components/cards/StatsCards.vue'
import LoaderDefault from '@/components/common/LoaderDefault.vue'
import api from '@/services/api'
import { getAxiosErrorMessage } from '@/utils/errorHandler'
import { notificationService } from '@/services/notificationService'
import EmptyState from '@/components/common/EmptyState.vue'
import TooltipPortal from '@/components/common/TooltipPortal.vue'

// ── Types ──────────────────────────────────────────────────────────────────
interface AssignedAgent { id: string; name: string; email?: string }
interface TenderWithAgent extends Tender {
  assignedAgent?: AssignedAgent
  assignedAgentId?: string
  agentWorkflowStatus?: WorkflowStatus
}

// ── State ──────────────────────────────────────────────────────────────────
const filters = ref({ global: { value: '', matchMode: FilterMatchMode.CONTAINS } })
const tenders          = ref<TenderWithAgent[]>([])
const selectedTenders  = ref<TenderWithAgent[]>([])
const loading          = ref(false)
const fetchError       = ref<string | null>(null)
const first            = ref(0)
const rows             = ref(20)
const showUploadModal  = ref(false)
const lastUploadDate   = ref<string | null>(null)
const loadingLastUpload = ref(false)
const previousTenderCount = ref(0)
const newTenderCount = ref(parseInt(localStorage.getItem('newTenderCount') || '0'))

// Modal loading states
const assigningAgent    = ref(false)
const bulkAssigning     = ref(false)
const addingToWatchlist = ref(false)
const rejectingTender   = ref(false)

// Rejection Modal
const isRejectionModalOpen             = ref(false)
const selectedTenderForRejection       = ref<TenderWithAgent | null>(null)
const selectedTendersForBulkRejection  = ref<TenderWithAgent[]>([])
const rejectionModalRef                = ref<InstanceType<typeof RejectionModal> | null>(null)

// Watchlist Modal
const isWatchlistModalOpen             = ref(false)
const selectedTenderForWatchlist       = ref<TenderWithAgent | null>(null)
const selectedTendersForBulkWatchlist  = ref<TenderWithAgent[]>([])
const watchlistModalRef                = ref<InstanceType<typeof WatchlistModal> | null>(null)

// Single Agent Assignment Modal
const isAgentAssignmentModalOpen      = ref(false)
const selectedTenderForAssignment     = ref<TenderWithAgent | null>(null)
const agentAssignmentModalRef         = ref<InstanceType<typeof AgentAssignmentModal> | null>(null)

// Bulk Agent Assignment Modal
const isBulkAgentAssignmentModalOpen  = ref(false)
const selectedTendersForBulkAssignment = ref<TenderWithAgent[]>([])
const bulkAgentAssignmentModalRef     = ref<InstanceType<typeof AgentAssignmentModal> | null>(null)

// ── Computed ───────────────────────────────────────────────────────────────
const allSelected = computed({
  get: () => tenders.value.length > 0 && tenders.value.every(t => t.selected),
  set: (value: boolean) => {
    tenders.value.forEach(t => { t.selected = value })
    selectedTenders.value = value ? [...tenders.value] : []
  }
})

const sortedTenders = computed(() =>
  [...tenders.value].sort((a, b) => {
    const dA = a.deadline ? new Date(a.deadline).getTime() : Infinity
    const dB = b.deadline ? new Date(b.deadline).getTime() : Infinity
    return dA - dB
  })
)

const paginatedTenders = computed(() =>
  sortedTenders.value.slice(first.value, first.value + rows.value)
)

// const totalDuplicateCount  = computed(() => tenders.value.filter(t => t.status === 'duplicate').length)
// const updatedTenderCount   = computed(() => tenders.value.filter(t => t.status === 'updated').length)

const expiredTendersInView = computed(() => {
  const now = new Date()
  return paginatedTenders.value.filter(t => {
    if (!t.deadline) return false
    try { return new Date(t.deadline) < now } catch { return false }
  })
})

// ── Helpers ────────────────────────────────────────────────────────────────
const formatNumber = (num: number): string => {
  if (num >= 10000000) return (num / 10000000).toFixed(2) + ' Cr'
  if (num >= 100000)   return (num / 100000).toFixed(2) + ' L'
  if (num >= 1000)     return (num / 1000).toFixed(2) + ' K'
  return num.toLocaleString('en-IN')
}

const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'
  try {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    })
  } catch { return dateString }
}

const getDaysRemaining = (deadline: string): string => {
  if (!deadline) return 'No deadline'
  try {
    const diff = Math.ceil((new Date(deadline).getTime() - Date.now()) / 86400000)
    if (diff < 0)  return 'Expired'
    if (diff === 0) return 'Today'
    if (diff === 1) return 'Tomorrow'
    return `${diff} days left`
  } catch { return 'Date error' }
}

const getDeadlineClass = (deadline: string): string => {
  if (!deadline) return 'text-gray-600'
  try {
    const diff = Math.ceil((new Date(deadline).getTime() - Date.now()) / 86400000)
    if (diff < 0)   return 'text-red-600 font-medium'
    if (diff <= 7)  return 'text-orange-600 font-medium'
    return 'text-green-600 font-medium'
  } catch { return 'text-gray-600' }
}

const getStatusLabel      = (status: Tender['status']) => getWorkflowLabel(status)
const getStatusBadgeClass = (status: Tender['status']) => 'p-badge-' + getBadgeStatus(status)

// ── Fetch ──────────────────────────────────────────────────────────────────
const fetchActiveTenders = async () => {
  try {
    loading.value = true
    fetchError.value = null
    const response = await api.get<TenderWithAgent[]>('/api/tenders/active', { params: { _: Date.now() } })
    const unassigned = response.data.filter(t => !t.assignedAgentId && !t.assignedAgent)
    tenders.value = unassigned.map(tender => ({
      ...tender,
      selected: false,
      assignedAgentName: tender.assignedAgent?.name || tender.assignedAgentName,
      agentWorkflowStatus: (tender.workflowStatus || tender.status) as WorkflowStatus
    }))
  } catch (error) {
    console.error(error)
    fetchError.value = getAxiosErrorMessage(error)
    tenders.value = []
  } finally { loading.value = false }
}

const fetchLastUploadDate = async () => {
  try {
    loadingLastUpload.value = true
    const r = await api.get('/api/upload/last')
    if (r.data?.lastUpload) lastUploadDate.value = r.data.lastUpload
  } catch { /* silent */ }
  finally { loadingLastUpload.value = false }
}

// ── Actions ────────────────────────────────────────────────────────────────
const handleCheckboxChange = (tender: TenderWithAgent, isChecked: boolean) => {
  tender.selected = isChecked
  if (isChecked) {
    selectedTenders.value.push(tender)
  } else {
    selectedTenders.value = selectedTenders.value.filter(t => t.t247Id !== tender.t247Id)
  }
}

const viewTenderDetails = (tender: Tender) => {
  router.push({ path: `/tenders/${tender.t247Id}`, query: { referrer: 'dashboard' } })
}

const onPageChange = (event: { first: number; rows: number }) => {
  first.value = event.first
  rows.value  = event.rows
}

// ── Upload ─────────────────────────────────────────────────────────────────
const openUploadModal = () => {
  previousTenderCount.value = tenders.value.length
  showUploadModal.value = true
}
const closeUploadModal = () => { showUploadModal.value = false }

const handleTenderUploaded = async () => {
  lastUploadDate.value = new Date().toISOString()
  closeUploadModal()
  await fetchActiveTenders()

  const diff = Math.max(0, tenders.value.length - previousTenderCount.value)
  newTenderCount.value = diff
  // Persist so it survives refresh
  localStorage.setItem('newTenderCount', diff.toString())

  try {
    await api.post('/api/upload/record', { uploadedAt: new Date().toISOString() })
  } catch { /* silent */ }

  setTimeout(() => notificationService.success('Upload completed! Data has been refreshed.', 5000), 500)
}

// ── Watchlist ──────────────────────────────────────────────────────────────
const openWatchlistModal = (tender: TenderWithAgent) => {
  if (tender.status === 'rejected') { notificationService.warning('Cannot add rejected tender to watchlist'); return }
  selectedTenderForWatchlist.value = tender
  selectedTendersForBulkWatchlist.value = []
  isWatchlistModalOpen.value = true
}

const openBulkWatchlistModal = () => {
  if (selectedTenders.value.length === 0) { notificationService.warning('Please select tenders first'); return }
  const valid = selectedTenders.value.filter(t => t.status !== 'rejected')
  if (valid.length === 0) { notificationService.warning('Cannot add rejected tenders to watchlist'); return }
  if (valid.length < selectedTenders.value.length)
    notificationService.info(`${selectedTenders.value.length - valid.length} rejected tender(s) were skipped`)
  selectedTendersForBulkWatchlist.value = [...valid]
  selectedTenderForWatchlist.value = null
  isWatchlistModalOpen.value = true
}

const handleModalWatchlistAdd = () => { watchlistModalRef.value?.submitForm() }
const handleCancelWatchlist = () => {
  isWatchlistModalOpen.value = false
  selectedTenderForWatchlist.value = null
  selectedTendersForBulkWatchlist.value = []
}

const handleAddToWatchlistSubmit = async (data: { tenderIds: string[]; notes: string }) => {
  addingToWatchlist.value = true
  try {
    let success = 0, fail = 0
    for (const id of data.tenderIds) {
      try {
        const r = await api.put(`/api/tenders/${id}/watchlist`, { isWatchlisted: true, watchlistNotes: data.notes || undefined })
        if (r.data.success) {
          success++
        } else {
          fail++
        }
      } catch {
        fail++
      }
    }
    if (success > 0) {
      const ids = new Set(data.tenderIds)
      tenders.value = tenders.value.filter(t => !ids.has(t.t247Id))
      if (success === data.tenderIds.length) {
        notificationService.success(`${success} tender(s) added to watchlist successfully!`, 4000)
      } else {
        notificationService.warning(`${success} added, ${fail} failed.`, 4000)
      }
    } else {
      notificationService.error('Failed to add tenders to watchlist')
    }
    selectedTenders.value = selectedTenders.value.filter(t => !data.tenderIds.includes(t.t247Id))
    handleCancelWatchlist()
  } catch (e) {
    notificationService.error(`Failed: ${getAxiosErrorMessage(e)}`)
  } finally { addingToWatchlist.value = false }
}

// ── Bulk Agent Assignment ──────────────────────────────────────────────────
const openBulkAgentAssignmentModal = () => {
  if (selectedTenders.value.length === 0) { notificationService.warning('Please select tenders first'); return }
  const valid = selectedTenders.value.filter(t => t.status !== 'rejected' && !t.assignedAgentId && !t.assignedAgent)
  if (valid.length === 0) { notificationService.warning('No valid tenders to assign'); return }
  if (valid.length < selectedTenders.value.length)
    notificationService.info(`${selectedTenders.value.length - valid.length} tender(s) skipped`)
  selectedTendersForBulkAssignment.value = [...valid]
  isBulkAgentAssignmentModalOpen.value = true
}

const handleBulkAgentAssignment = async (data: { tenderId: string; agentId: string; agentName: string; notes: string }) => {
  bulkAssigning.value = true
  try {
    let success = 0, fail = 0
    const failed: string[] = []
    for (const tender of selectedTendersForBulkAssignment.value) {
      try {
        const r = await api.put('/api/tenders/assign-agent', {
          tenderId: tender.t247Id, agentId: data.agentId, agentName: data.agentName,
          notes: data.notes, status: 'assigned-to-agent', workflowStatus: 'assigned-to-agent',
          assignedAt: new Date().toISOString()
        })
        if (r.data.success) {
          success++
        } else {
          fail++
          failed.push(tender.referenceNo || tender.t247Id)
        }
      } catch { fail++; failed.push(tender.referenceNo || tender.t247Id) }
    }
    await fetchActiveTenders()
    if (success > 0) {
      if (success === selectedTendersForBulkAssignment.value.length) {
        notificationService.success(`All ${success} tender(s) assigned to ${data.agentName}!`, 5000)
      } else {
        notificationService.warning(`${success} assigned, ${fail} failed. Failed: ${failed.join(', ')}`, 8000)
      }
    } else {
      notificationService.error('Failed to assign any tenders')
    }
    selectedTenders.value = selectedTenders.value.filter(
      t => !selectedTendersForBulkAssignment.value.some(s => s.t247Id === t.t247Id)
    )
    isBulkAgentAssignmentModalOpen.value = false
    selectedTendersForBulkAssignment.value = []
  } catch (e) {
    notificationService.error(`Failed: ${getAxiosErrorMessage(e)}`)
  } finally { bulkAssigning.value = false }
}

const handleModalBulkAssign = () => { bulkAgentAssignmentModalRef.value?.submitForm() }
const handleCancelBulkAssignment = () => {
  if (bulkAssigning.value) return
  isBulkAgentAssignmentModalOpen.value = false
  selectedTendersForBulkAssignment.value = []
}

// ── Rejection ──────────────────────────────────────────────────────────────
const openRejectTenderModal = (tender: TenderWithAgent) => {
  if (tender.status === 'rejected') { notificationService.info('Already rejected'); return }
  selectedTenderForRejection.value = tender
  isRejectionModalOpen.value = true
}
const openBulkRejectModal = () => {
  if (selectedTenders.value.length === 0) { notificationService.warning('Please select tenders first'); return }
  selectedTendersForBulkRejection.value = [...selectedTenders.value]
  isRejectionModalOpen.value = true
}
const closeRejectionModal = () => {
  isRejectionModalOpen.value = false
  selectedTenderForRejection.value = null
  selectedTendersForBulkRejection.value = []
}
const handleModalReject = () => { rejectionModalRef.value?.submitForm() }

const handleTenderRejection = async (data: { tenderId: string | string[]; comment: string }) => {
  rejectingTender.value = true
  try {
    const ids = Array.isArray(data.tenderId) ? data.tenderId : [data.tenderId]
    await api.put('/api/tenders/update-status', { tenderIds: ids, status: 'rejected', rejectionComment: data.comment })
    await fetchActiveTenders()
    selectedTenders.value = []
    notificationService.success(`${ids.length} tender(s) rejected successfully!`, 4000)
    closeRejectionModal()
  } catch (e) {
    notificationService.error(`Failed: ${getAxiosErrorMessage(e)}`)
  } finally { rejectingTender.value = false }
}

// ── Single Agent Assignment ────────────────────────────────────────────────
const openAgentAssignmentModal = (tender: TenderWithAgent) => {
  if (tender.status === 'rejected') { notificationService.warning('Cannot assign agent to rejected tender'); return }
  selectedTenderForAssignment.value = tender
  isAgentAssignmentModalOpen.value = true
}
const handleModalAssign = () => { agentAssignmentModalRef.value?.submitForm() }
const handleCancelAssignment = () => {
  if (assigningAgent.value) return
  isAgentAssignmentModalOpen.value = false
}

const handleAgentAssignment = async (data: { tenderId: string; agentId: string; agentName: string; notes: string }) => {
  assigningAgent.value = true
  try {
    const r = await api.put('/api/tenders/assign-agent', {
      tenderId: data.tenderId, agentId: data.agentId, agentName: data.agentName,
      notes: data.notes, status: 'assigned-to-agent', workflowStatus: 'assigned-to-agent',
      assignedAt: new Date().toISOString()
    })
    if (r.data.success) {
      await fetchActiveTenders()
      notificationService.success(`Agent "${data.agentName}" assigned successfully!`, 4000)
      isAgentAssignmentModalOpen.value = false
      if (selectedTenderForAssignment.value) {
        selectedTenderForAssignment.value.selected = false
        selectedTenders.value = selectedTenders.value.filter(t => t.t247Id !== selectedTenderForAssignment.value?.t247Id)
      }
    } else { throw new Error(r.data.error || 'Assignment failed') }
  } catch (e) {
    notificationService.error(`Failed: ${getAxiosErrorMessage(e)}`)
  } finally { assigningAgent.value = false }
}

// ── Tooltip ────────────────────────────────────────────────────────────────
const tooltipVisible  = ref(false)
const tooltipText     = ref('')
const tooltipTarget   = ref<HTMLElement>()

const showTooltip = (event: MouseEvent, text: string) => {
  tooltipTarget.value  = event.currentTarget as HTMLElement
  tooltipText.value    = text
  tooltipVisible.value = true
}
const hideTooltip = () => {
  tooltipVisible.value = false
  tooltipTarget.value  = undefined
  tooltipText.value    = ''
}

onMounted(() => {
  fetchActiveTenders()
  fetchLastUploadDate()
})
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="Tender Management" :icon="TagIcon" />

    <div class="space-y-6">
      <!-- Stats Row -->
      <!-- <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatsCards title="Active Tenders"    :value="tenders.length"       :icon="SigmaIcon"   />
        <StatsCards title="New Tenders" :value="newTenderCount" :icon="TagIcon" />
        <StatsCards title="Updated Tenders"   :value="updatedTenderCount"   :icon="RefreshIcon" />
        <StatsCards title="Duplicate Tenders" :value="totalDuplicateCount"  :icon="CloneIcon"   />
      </div> -->

      <!-- Error -->
      <div v-if="fetchError" class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-center gap-3">
          <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <p class="text-sm font-medium text-red-800">{{ fetchError }}</p>
          <button @click="fetchActiveTenders" class="text-sm text-red-700 hover:text-red-600 underline">Retry</button>
        </div>
      </div>

      <!-- Tender List -->
      <CardComponent
        className="max-w-full"
        bodyClass="flex-col items-center justify-center"
        footerClass="justify-end"
        title="Active Tender List (Unassigned)"
        :showHeaderBorder="true"
        :showHeaderPaddingBottom="true"
        :headerIcon="TagIcon"
      >
        <template #header>
          <div class="flex items-center justify-between gap-2">
            <TextInputDefault
              v-model="filters.global.value"
              placeholder="Search active tenders..."
              size="xss"
              type="text"
              id="search-tenders"
              name="search-tenders"
              class="flex-1"
            />
            <div class="flex gap-2">
              <BaseButton v-if="selectedTenders.length > 0" size="xss" :icon="BookMarkIcon" variant="info"    @click="openBulkWatchlistModal"        tooltip="Add selected to watchlist" />
              <BaseButton v-if="selectedTenders.length > 0" size="xss" :icon="AgentIcon"    variant="primary" @click="openBulkAgentAssignmentModal"   tooltip="Assign selected to agent" />
              <BaseButton v-if="selectedTenders.length > 0" size="xss" :icon="BanIcon"      variant="danger"  @click="openBulkRejectModal"            tooltip="Reject selected" />
              <BaseButton                                    size="xss" :icon="UploadIcon"                     @click="openUploadModal"                tooltip="Upload Tenders" />
              <BaseButton                                    size="xss" :icon="RefreshIcon"                    @click="fetchActiveTenders"             tooltip="Refresh" />
            </div>
          </div>
        </template>

        <template #body>
          <LoaderDefault v-if="loading" title="Loading active tenders..." />

          <EmptyState
            v-else-if="tenders.length === 0 && !fetchError"
            :icon="GlobeIcon"
            title="All tenders have been assigned or processed"
            action-text="Upload Tenders"
            :action-icon="UploadIcon"
            @action="openUploadModal"
          />

          <DataTable
            v-if="tenders.length > 0 && !loading"
            :filters="filters"
            :value="paginatedTenders"
            class="w-full"
            sortMode="multiple"
            scrollable
            scrollHeight="450px"
            resizableColumns
            columnResizeMode="fit"
            showGridlines
            :pt="{
              table: { style: 'border-collapse: separate; border-spacing: 0;' },
              thead: { style: 'position: sticky; top: 0; z-index: 20;' },
              tbody: { style: 'position: relative; z-index: 10;' }
            }"
          >
            <Column
              headerStyle="width: 48px; position: sticky; left: 0; z-index: 30;"
              bodyStyle="position: sticky; left: 0; z-index: 25;"
              frozen
            >
              <template #header><CheckBoxDefault v-model="allSelected" /></template>
              <template #body="{ data }">
                <CheckBoxDefault v-model="data.selected" @update:modelValue="(val: boolean) => handleCheckboxChange(data, val)" />
              </template>
            </Column>

            <Column sortable frozen field="referenceNo" header="Reference No"
              headerStyle="position: sticky; left: 48px; z-index: 30;"
              bodyStyle="position: sticky; left: 48px; z-index: 25;"
            >
              <template #body="{ data }">
                <div class="flex flex-col max-w-[100px] text-xs">
                  <a v-if="data.tenderBriefLink" :href="data.tenderBriefLink" target="_blank"
                    class="font-medium hover:underline flex items-center gap-1 break-all whitespace-normal">
                    {{ data.referenceNo }}
                    <ExternalIcon class="w-3 h-3 text-green-600 shrink-0" />
                  </a>
                  <span v-else class="break-all whitespace-normal">{{ data.referenceNo }}</span>
                  <span class="text-xs text-gray-500 break-all">{{ data.t247Id }}</span>
                </div>
              </template>
            </Column>

            <Column field="deadline" header="Deadline" sortable>
              <template #body="{ data }">
                <div class="flex flex-col text-xs">
                  <span :class="getDeadlineClass(data.deadline)">{{ formatDate(data.deadline) }}</span>
                  <span>{{ getDaysRemaining(data.deadline) }}</span>
                </div>
              </template>
            </Column>

            <Column sortable field="sheetType" header="Type">
              <template #body="{ data }">
                <Badge :value="data.sheetType" :class="'text-xs p-badge-' + getBadgeStatus(data.sheetType?.toLowerCase())" />
              </template>
            </Column>

            <Column field="tenderBrief" header="Description" sortable>
              <template #body="{ data }">
                <div class="line-clamp-2 text-xs cursor-help"
                  @mouseenter="(e) => showTooltip(e, data.tenderBrief)"
                  @mouseleave="hideTooltip">
                  {{ data.tenderBrief }}
                </div>
              </template>
            </Column>

            <Column field="organization" header="Organization" sortable>
              <template #body="{ data }"><div class="text-xs">{{ data.organization }}</div></template>
            </Column>

            <Column sortable field="location" header="Location">
              <template #body="{ data }"><div class="text-xs">{{ data.location }}</div></template>
            </Column>

            <Column field="estimatedCost" header="Estimated Cost" sortable>
              <template #body="{ data }"><div class="font-medium">₹{{ formatNumber(data.estimatedCost) }}</div></template>
            </Column>

            <Column field="status" header="Tender Status" sortable>
              <template #body="{ data }">
                <Badge :value="getStatusLabel(data.status)" :class="getStatusBadgeClass(data.status)" />
              </template>
            </Column>

            <Column frozen field="action" header="Actions"
              headerStyle="width: 155px; position: sticky; right: 0; z-index: 30;"
              bodyStyle="position: sticky; right: 0; z-index: 25;"
            >
              <template #body="{ data }">
                <div class="flex gap-2">
                  <BaseButton size="xss" :icon="EyeIcon"      variant="secondary" tooltip="View Details"    @click="viewTenderDetails(data)" />
                  <BaseButton v-if="!data.assignedAgent && !data.assignedAgentName"
                              size="xss" :icon="AgentIcon"    variant="secondary" tooltip="Assign Agent"    @click="openAgentAssignmentModal(data)" :disabled="selectedTenders.length > 0" />
                  <BaseButton size="xss" :icon="BookMarkIcon" variant="secondary" tooltip="Add to Watchlist" @click="openWatchlistModal(data)" :disabled="selectedTenders.length > 0" />
                  <BaseButton size="xss" :icon="BanIcon"      variant="danger"    tooltip="Reject"          @click="openRejectTenderModal(data)" :disabled="selectedTenders.length > 0" />
                </div>
              </template>
            </Column>
          </DataTable>
        </template>

        <template #footer v-if="tenders.length > 0 && !loading">
          <div class="flex justify-between items-center w-full">
            <div class="text-xs text-gray-500">
              Showing {{ first + 1 }}-{{ Math.min(first + rows, tenders.length) }} of {{ tenders.length }} tenders
              <span v-if="expiredTendersInView.length > 0" class="text-yellow-600 ml-2">
                ({{ expiredTendersInView.length }} expired in view)
              </span>
            </div>
            <Paginator
              v-model:first="first"
              v-model:rows="rows"
              :rowsPerPageOptions="[5, 10, 20, 50]"
              :totalRecords="tenders.length"
              @page="onPageChange"
            />
          </div>
        </template>
      </CardComponent>
    </div>
  </AdminLayout>

  <!-- Upload Modal -->
  <ModalDefault v-model="showUploadModal" title="Upload Excel File" @close="closeUploadModal">
    <TenderUpload @tenderUploaded="handleTenderUploaded" @close="closeUploadModal" />
  </ModalDefault>

  <!-- Single Agent Assignment Modal -->
  <ModalDefault v-model="isAgentAssignmentModalOpen" title="Assign Tender to Agent" width="max-w-lg" classBody="my-4" :loading="assigningAgent">
    <AgentAssignmentModal v-if="selectedTenderForAssignment" ref="agentAssignmentModalRef"
      :tender="selectedTenderForAssignment" :currentAgentId="selectedTenderForAssignment.assignedAgentId"
      @assign="handleAgentAssignment" @cancel="handleCancelAssignment" />
    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton text="Cancel" variant="secondary" :disabled="assigningAgent" @click="handleCancelAssignment" />
        <BaseButton text="Assign Tender" variant="primary" :loading="assigningAgent" loading-text="Assigning…" @click="handleModalAssign" />
      </div>
    </template>
  </ModalDefault>

  <!-- Bulk Agent Assignment Modal -->
  <ModalDefault v-model="isBulkAgentAssignmentModalOpen" title="Bulk Assign Tenders to Agent" width="max-w-lg" classBody="my-4"
    @close="handleCancelBulkAssignment" :loading="bulkAssigning">
    <AgentAssignmentModal v-if="isBulkAgentAssignmentModalOpen && selectedTendersForBulkAssignment.length > 0"
      ref="bulkAgentAssignmentModalRef" :tender="selectedTendersForBulkAssignment[0]" :currentAgentId="undefined"
      @assign="handleBulkAgentAssignment" @cancel="handleCancelBulkAssignment" />
    <div v-if="selectedTendersForBulkAssignment.length > 0" class="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-medium text-blue-800">Selected Tenders: {{ selectedTendersForBulkAssignment.length }}</h3>
        <span class="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">Bulk Action</span>
      </div>
      <ul class="space-y-1 text-sm max-h-32 overflow-y-auto">
        <li v-for="tender in selectedTendersForBulkAssignment" :key="tender.t247Id"
          class="flex justify-between py-1 border-b border-blue-200 last:border-0">
          <span class="text-blue-600">{{ tender.referenceNo }}</span>
          <span class="text-blue-800 font-medium">{{ tender.tenderBrief?.substring(0, 30) }}...</span>
        </li>
      </ul>
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton text="Cancel" variant="secondary" :disabled="bulkAssigning" @click="handleCancelBulkAssignment" />
        <BaseButton text="Assign to All Selected" variant="primary" :loading="bulkAssigning" loading-text="Assigning…" @click="handleModalBulkAssign" />
      </div>
    </template>
  </ModalDefault>

  <!-- Watchlist Modal -->
  <ModalDefault v-model="isWatchlistModalOpen"
    :title="selectedTendersForBulkWatchlist.length > 0 ? 'Add to Watchlist (Bulk)' : 'Add to Watchlist'"
    width="max-w-lg" classBody="my-4" @close="handleCancelWatchlist" :loading="addingToWatchlist">
    <WatchlistModal v-if="isWatchlistModalOpen" ref="watchlistModalRef"
      :tender="selectedTenderForWatchlist" :tenders="selectedTendersForBulkWatchlist"
      @addToWatchlist="handleAddToWatchlistSubmit" @cancel="handleCancelWatchlist" />
    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton text="Cancel" variant="secondary" :disabled="addingToWatchlist" @click="handleCancelWatchlist" />
        <BaseButton text="Add to Watchlist" variant="info" :loading="addingToWatchlist" loading-text="Adding…" @click="handleModalWatchlistAdd" />
      </div>
    </template>
  </ModalDefault>

  <!-- Rejection Modal -->
  <ModalDefault v-model="isRejectionModalOpen" title="Reject Tender" :loading="rejectingTender">
    <RejectionModal v-if="isRejectionModalOpen" ref="rejectionModalRef"
      :tender="selectedTenderForRejection" :tenders="selectedTendersForBulkRejection"
      @reject="handleTenderRejection" @cancel="closeRejectionModal" :loading="rejectingTender" />
    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton text="Cancel" variant="secondary" :disabled="rejectingTender" @click="closeRejectionModal" />
        <BaseButton
          :text="selectedTendersForBulkRejection.length > 0 ? `Reject ${selectedTendersForBulkRejection.length} Tenders` : 'Reject Tender'"
          variant="danger" :loading="rejectingTender" loading-text="Rejecting…" @click="handleModalReject" />
      </div>
    </template>
  </ModalDefault>

  <TooltipPortal :visible="tooltipVisible" :text="tooltipText" :target="tooltipTarget" position="auto" :max-width="'400px'" :multiline="true" />
</template>
